/**
 * CimetiereSection — bloc pliable « Cimetière » du pôle info (administré).
 *
 * Affiché seulement si la mairie a au moins un cimetière OU si l'administré a
 * déjà des demandes (backend renvoie `cimetiere: null` sinon → masquage auto).
 * L'administré peut déposer une demande (nouvelle concession / renouvellement)
 * et suivre ses demandes. Accusé de réception géré côté serveur (helper central).
 *
 * Aucune modale navigateur (Règle projet) — retours via message inline.
 */

import { useState } from 'react';
import { Landmark, Send } from 'lucide-react';
import { SectionRepliable } from '@/components/ui/SectionRepliable';
import { Badge, Button, Field } from '@/components/ui';
import { api, ApiException, type CimetiereCitoyenPayload } from '@/lib/api';

export function CimetiereSection({
  data, onUpdated,
}: {
  data: CimetiereCitoyenPayload;
  onUpdated: (next: CimetiereCitoyenPayload | null) => void;
}) {
  const [ouvrir, setOuvrir] = useState(false);
  const [typeDemande, setTypeDemande] = useState<'nouvelle' | 'renouvellement'>('nouvelle');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function envoyer() {
    setBusy(true); setMsg(null);
    try {
      await api.cimetiere.creerDemande({ typeDemande, message: message.trim() || null });
      setMessage(''); setOuvrir(false);
      setMsg('Demande envoyée. La mairie vous répondra ; vous la retrouvez ci-dessous.');
      try { onUpdated(await api.cimetiere.get()); } catch { /* ignore */ }
    } catch (e) {
      setMsg(e instanceof ApiException ? e.message : 'Envoi impossible');
    } finally { setBusy(false); }
  }

  const enAttente = data.mesDemandes.filter((d) => d.statut === 'nouvelle' || d.statut === 'en_cours').length;
  const selectCls = 'w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc';

  return (
    <SectionRepliable
      title="Cimetière — concessions"
      icon={<Landmark className="h-5 w-5 text-grc" aria-hidden="true" />}
      defaultOpen={false}
      right={enAttente > 0
        ? <Badge variant="warning" size="sm">{enAttente} en cours</Badge>
        : <Badge variant="neutral" size="sm">Démarches</Badge>}
    >
      {/* Mes demandes */}
      {data.mesDemandes.length > 0 && (
        <div className="mb-3">
          <div className="mb-1 text-xs font-medium text-ink-muted">Mes demandes</div>
          <ul className="space-y-2">
            {data.mesDemandes.map((d) => (
              <li key={d.id} className="rounded-r-lg border border-border bg-surface/50 p-3">
                <div className="flex items-center gap-2 font-medium text-ink">
                  {d.typeDemande === 'renouvellement' ? 'Renouvellement' : 'Nouvelle concession'}
                  {d.statut === 'nouvelle' && <Badge variant="warning" size="sm">Reçue</Badge>}
                  {d.statut === 'en_cours' && <Badge variant="neutral" size="sm">En cours</Badge>}
                  {d.statut === 'traitee' && <Badge variant="success" size="sm">Traitée</Badge>}
                  {d.statut === 'refusee' && <Badge variant="urgent" size="sm">Refusée</Badge>}
                </div>
                {d.reponse && <p className="mt-0.5 text-sm text-ink-muted">Réponse : {d.reponse}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Nouvelle demande */}
      {!ouvrir ? (
        <Button variant="ghost" size="sm" onClick={() => setOuvrir(true)}>
          <Send className="h-4 w-4" /> Faire une demande
        </Button>
      ) : (
        <div className="space-y-2 rounded-r-lg border border-border bg-bg-2 p-3">
          <Field label="Type de demande" htmlFor="cim-type">
            <select id="cim-type" className={selectCls} value={typeDemande} onChange={(e) => setTypeDemande(e.target.value as 'nouvelle' | 'renouvellement')}>
              <option value="nouvelle">Nouvelle concession</option>
              <option value="renouvellement">Renouvellement d'une concession existante</option>
            </select>
          </Field>
          <Field label="Message (optionnel)" htmlFor="cim-msg">
            <textarea
              id="cim-msg" rows={3}
              className="w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc"
              value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Précisez votre besoin (type de concession souhaité, n° de concession à renouveler…)."
            />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOuvrir(false)} disabled={busy}>Annuler</Button>
            <Button variant="primary" size="sm" onClick={envoyer} disabled={busy}>{busy ? 'Envoi…' : 'Envoyer'}</Button>
          </div>
        </div>
      )}

      {msg && <p className="mt-2 text-xs text-ink-muted" role="status">{msg}</p>}
    </SectionRepliable>
  );
}
